// src/utils/authTokenManager.ts

/**
 * Manages authentication tokens for API requests
 */
export class AuthTokenManager {
  private static instance: AuthTokenManager;
  private tokenStorageKey = 'authToken';
  
  private constructor() {}
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): AuthTokenManager {
    if (!AuthTokenManager.instance) {
      AuthTokenManager.instance = new AuthTokenManager();
    }
    return AuthTokenManager.instance;
  }
  
  /**
   * Get the current auth token
   */
  public getToken(): string | null {
    return localStorage.getItem(this.tokenStorageKey);
  }
  
  /**
   * Check if the token is valid and not expired
   */
  public isTokenValid(token: string): boolean {
    if (!token) return false;
    
    try {
      // Basic JWT token validation (check expiration)
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      const payload = JSON.parse(atob(parts[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      
      return Date.now() < expiry;
    } catch (error) {
      console.error('[AuthTokenManager] Error validating token:', error);
      return false;
    }
  }
  
  /**
   * Get a valid token, refreshing if necessary
   */
  public async getValidToken(): Promise<string | null> {
    const token = this.getToken();
    
    if (!token) return null;
    
    if (this.isTokenValid(token)) {
      return token;
    }
    
    // Token is invalid or expired, try to refresh
    try {
      const newToken = await this.refreshToken();
      return newToken;
    } catch (error) {
      console.error('[AuthTokenManager] Error refreshing token:', error);
      return null;
    }
  }
  
  /**
   * Refresh the token
   */
  public async refreshToken(): Promise<string | null> {
    try {
      // Use your existing auth service to refresh the token
      // This would typically call your refresh token endpoint
      
      // Example:
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: localStorage.getItem('refreshToken')
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to refresh token: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.token) {
        localStorage.setItem(this.tokenStorageKey, data.token);
        
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        
        return data.token;
      }
      
      return null;
    } catch (error) {
      console.error('[AuthTokenManager] Error refreshing token:', error);
      return null;
    }
  }
  
  /**
   * Check if the user is authenticated
   */
  public isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null && this.isTokenValid(token);
  }
  
  /**
   * Clear authentication tokens
   */
  public clearTokens(): void {
    localStorage.removeItem(this.tokenStorageKey);
    localStorage.removeItem('refreshToken');
  }
}

export const authTokenManager = AuthTokenManager.getInstance();
